import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Loader2, Trash2 } from 'lucide-react';
import { attendanceApi } from '../services/api';
import { formatCompanyDateRange } from '../utils/companyDateFormat';
import type { AttendanceBulkDeleteBlockedItem, AttendanceRecord } from '../types';
import { SingleDayCalendarPicker } from './SingleDayCalendarPicker';
import { extractDatePart, formatDisplayTime } from '../utils/datetime';

export interface BulkDeleteAttendanceModalProps {
  open: boolean;
  onClose: () => void;
  locationId: number;
  /** Inclusive yyyy-MM-dd (Attendance page week start) */
  weekStartDate: string;
  /** Inclusive yyyy-MM-dd (Attendance page week end) */
  weekEndDate: string;
  /** Matches Attendance `week_start_day` / date-fns (0=Sunday … 6=Saturday) */
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  onDeleted?: () => void;
}

function sameCalendarDay(clockInIso: string, dayYyyyMmDd: string): boolean {
  return extractDatePart(clockInIso) === dayYyyyMmDd;
}

const BulkDeleteAttendanceModal: React.FC<BulkDeleteAttendanceModalProps> = ({
  open,
  onClose,
  locationId,
  weekStartDate,
  weekEndDate,
  weekStartsOn,
  onDeleted,
}) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<AttendanceBulkDeleteBlockedItem[] | null>(null);

  const weekRangeLabel = useMemo(() => {
    try {
      return formatCompanyDateRange(weekStartDate, weekEndDate);
    } catch {
      return '';
    }
  }, [weekStartDate, weekEndDate]);

  const visibleRecords = useMemo(() => {
    if (!selectedDate) return [];
    return records.filter(
      (r) => !r.deletedAt && r.clockInTime && sameCalendarDay(r.clockInTime, selectedDate)
    );
  }, [records, selectedDate]);

  const loadForDate = useCallback(async () => {
    if (!selectedDate) {
      setRecords([]);
      setSelectedIds(new Set());
      return;
    }
    setLoading(true);
    setError(null);
    setBlocked(null);
    try {
      const data = await attendanceApi.getAll({
        locationId,
        startDate: selectedDate,
        endDate: selectedDate,
      });
      setRecords(data);
      setSelectedIds(new Set());
    } catch {
      setError('Could not load attendance for that date.');
      setRecords([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [locationId, selectedDate]);

  useEffect(() => {
    if (open) {
      setError(null);
      setBlocked(null);
    }
  }, [open]);

  /** Keep selected day within the Attendance page week only */
  useEffect(() => {
    if (!open || !weekStartDate || !weekEndDate) return;
    setSelectedDate((prev) => {
      if (!prev || prev < weekStartDate || prev > weekEndDate) {
        return weekStartDate;
      }
      return prev;
    });
  }, [open, weekStartDate, weekEndDate]);

  useEffect(() => {
    if (!open) return;
    void loadForDate();
  }, [open, loadForDate]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(visibleRecords.map((r) => r.id)));
  };

  const unselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `Permanently delete ${selectedIds.size} attendance record(s)? This cannot be undone.`
      )
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    setBlocked(null);
    try {
      await attendanceApi.importBulkDelete({
        locationId,
        attendanceRecordIds: Array.from(selectedIds),
      });
      onDeleted?.();
      onClose();
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string; blocked?: AttendanceBulkDeleteBlockedItem[] } } };
      if (ax.response?.status === 409 && ax.response.data?.blocked?.length) {
        setBlocked(ax.response.data.blocked);
        setError(
          ax.response.data.message ||
            'Cannot delete while the timecard for that week is approved. Unapprove the timecard first.'
        );
      } else {
        setError(ax.response?.data?.message || (err instanceof Error ? err.message : 'Delete failed.'));
      }
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Bulk delete attendance</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-800">Date</span>
            {weekRangeLabel ? (
              <span className="text-xs text-gray-500">Only days in the current week: {weekRangeLabel}</span>
            ) : null}
            <SingleDayCalendarPicker
              value={selectedDate}
              onChange={setSelectedDate}
              weekStartsOn={weekStartsOn}
              min={weekStartDate}
              max={weekEndDate}
              aria-label="Date within current week"
            />
          </label>

          {loading ? (
            <div className="flex items-center gap-2 text-gray-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading records…
            </div>
          ) : selectedDate && visibleRecords.length === 0 ? (
            <p className="text-sm text-gray-600">No attendance records for this date.</p>
          ) : selectedDate ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-800 hover:bg-gray-50"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={unselectAll}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-800 hover:bg-gray-50"
                >
                  Unselect all
                </button>
                <span className="text-sm text-gray-600">
                  {selectedIds.size} of {visibleRecords.length} selected
                </span>
              </div>

              <div className="min-h-0 flex-1 overflow-auto rounded border border-gray-200">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-gray-100 text-xs font-semibold uppercase text-gray-600">
                    <tr>
                      <th className="w-10 px-2 py-2" />
                      <th className="px-2 py-2">Employee</th>
                      <th className="px-2 py-2">In</th>
                      <th className="px-2 py-2">Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRecords.map((r) => {
                      const name = r.employee
                        ? `${r.employee.firstName} ${r.employee.lastName}`
                        : `Employee #${r.employeeId}`;
                      const cin = r.clockInTime ? formatDisplayTime(r.clockInTime) : '—';
                      const cout =
                        r.clockOutTime != null && r.clockOutTime !== ''
                          ? formatDisplayTime(r.clockOutTime)
                          : '—';
                      return (
                        <tr key={r.id} className="border-t border-gray-100">
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggle(r.id)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-2 py-2 text-gray-900">{name}</td>
                          <td className="px-2 py-2 tabular-nums text-gray-800">{cin}</td>
                          <td className="px-2 py-2 tabular-nums text-gray-800">{cout}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-600">Choose a date to list attendance records.</p>
          )}

          {error ? (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
              <p>{error}</p>
              {blocked && blocked.length > 0 ? (
                <ul className="mt-2 list-inside list-disc space-y-1 text-red-800">
                  {blocked.map((b) => (
                    <li key={`${b.employeeId}-${b.weekStartDate}`}>
                      {b.employeeName} — week starting {b.weekStartDate} (records:{' '}
                      {b.attendanceRecordIds.join(', ')})
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0 || deleting}
              onClick={() => void handleDelete()}
              className="inline-flex items-center gap-1.5 rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Bulk delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteAttendanceModal;
